import React, { useState } from 'react'
import dayjs from 'dayjs'
import { type DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { Box, Pagination, RowsSkeletonLoader } from '@node-space/ui-components'
import {
  EARN_ASSET_TRANSACTIONS_PAGE_SIZE,
  useEarnAssetTransactionsQuery,
} from '../hooks/api/useEarnAssetTransactionsQuery'
import { EarnAssetHistoryExportModal } from './EarnAssetHistoryExportModal'
import { EarnAssetHistoryFilters } from './EarnAssetHistoryFilters'
import { EarnAssetHistoryTable } from './EarnAssetHistoryTable'

export interface EarnAssetHistoryProps {
  portfolioId?: string
  walletId?: string
}

const DEFAULT_DATE_RANGE_MONTHS = 6

const getDefaultDateRange = (): DateRange => ({
  from: dayjs().subtract(DEFAULT_DATE_RANGE_MONTHS, 'month').toDate(),
  to: dayjs().toDate(),
})

export const EarnAssetHistory = ({ portfolioId, walletId }: EarnAssetHistoryProps) => {
  const { t } = useTranslation()
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange)
  const [page, setPage] = useState(0)
  const [showExportModal, setShowExportModal] = useState(false)

  const { data: transactionsData, isLoading } = useEarnAssetTransactionsQuery(portfolioId, page, {
    start: dateRange?.from ? dayjs(dateRange.from).startOf('day').toISOString() : undefined,
    end: dateRange?.to ? dayjs(dateRange.to).endOf('day').toISOString() : undefined,
  })

  const transactions = transactionsData?.content ?? []
  const total = transactionsData?.pagination?.total ?? 0

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range)
    setPage(0)
  }

  return (
    <Box flexCol gap={16} className="w-full" testId="earn-asset-history">
      <Box flexCol gap={24}>
        <h4 className="text-h4 text-foreground-prominent">{t('earn.myHistory')}</h4>

        <EarnAssetHistoryFilters
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          onExportClick={() => setShowExportModal(true)}
        />
      </Box>

      {showExportModal && (
        <EarnAssetHistoryExportModal
          handleClose={() => setShowExportModal(false)}
          walletId={walletId}
        />
      )}

      {isLoading ? (
        <RowsSkeletonLoader rows={5} />
      ) : (
        <>
          <EarnAssetHistoryTable transactions={transactions} />

          {total > EARN_ASSET_TRANSACTIONS_PAGE_SIZE && (
            <Pagination
              testId="earn-asset-history-pagination"
              page={page}
              rowsPerPage={EARN_ASSET_TRANSACTIONS_PAGE_SIZE}
              total={total}
              onChangePage={setPage}
            />
          )}
        </>
      )}
    </Box>
  )
}
